import { createContext, useContext, type FC } from 'react';
import styled from 'styled-components';
import { Form as AntdForm, type FormProps, type FormItemProps as AntdFormItemProps } from 'antd';
import theme from '@/theme';
import type { ValidateStatus } from 'antd/lib/form/FormItem';
import { Icon } from '@/components/icon';
import type { LabelProps } from '@/components/typography/components/Label';
import { Typography } from '@/components/typography';
import { TypographySize } from '@/components/typography/enums/size';
import { TypographyType } from '@/components/typography/enums/type';

export const VALIDATION_STATUS: Readonly<{ [K in ValidateStatus as Capitalize<K>]: K }> = {
  Success: 'success',
  Error: 'error',
  Warning: 'warning',
  Validating: 'validating',
  '': '',
};

const { Item: AntItem, ErrorList, List, Provider, useForm, useFormInstance, useWatch } = AntdForm;

const FormLayoutContext = createContext<FormProps['layout']>('horizontal');

/** Label size derived from the form layout. Exported so read-only renderers (e.g. Descriptions) match. */
export const DEFAULT_LABEL_SIZE_BY_LAYOUT: Record<NonNullable<FormProps['layout']>, TypographySize> = {
  vertical: TypographySize.Medium,
  horizontal: TypographySize.Large,
  inline: TypographySize.Large,
};

/** Label type derived from the form layout. Exported so read-only renderers (e.g. Descriptions) match. */
export const DEFAULT_LABEL_TYPE_BY_LAYOUT: Record<NonNullable<FormProps['layout']>, TypographyType> = {
  vertical: TypographyType.Secondary,
  horizontal: TypographyType.Normal,
  inline: TypographyType.Normal,
};

// Mirror of the input-control tokens in `AppAntConfigProvider` (Input/Descriptions): a read value box
// must line up with the editable control it replaces.
const CONTROL_HEIGHT = 32;
const CONTROL_BORDER_RADIUS = 4;

/**
 * Read-only value box that mirrors the app's input control (same height/radius/background as the
 * disabled ant Input from `AppAntConfigProvider`). Used where a form field is shown in read mode
 * without an actual control — e.g. `Descriptions` read cells — so read and edit states line up.
 */
export const ReadValue = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${CONTROL_HEIGHT}px;
  padding: 0 ${theme.spacing[3]};
  border-radius: ${CONTROL_BORDER_RADIUS}px;
  background-color: ${theme.color.semantic.background.base};
`;

const StyledForm = styled(AntdForm)<FormProps>`
  .ant-form-item-label {
    label {
      white-space: normal;
      word-break: break-word;
      text-align: start;
      ${({ layout }) => layout !== 'vertical' && `width: 170px; max-width: 100%`};

      // Remove default AntdD * symbol from label
      ::before {
        display: none !important;
      }

      // Remove : symbol from label
      ::after {
        content: none;
        // Add * symbol to required fields when layout of form is vertical
        visibility: visible;
      }

      // Add * symbol to required fields
      &.ant-form-item-required::after {
        visibility: visible !important;
        content: ' *';
        color: ${theme.color.semantic.feedback.type.error};
        font-size: ${theme.font.size.xs}px;
      }
    }

    // We use custom label renderer
    label label {
      width: unset;
    }
  }

  .ant-form-item:last-of-type {
    margin-bottom: 0;
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    font-size: ${theme.font.size.xxs}px;
    line-height: 1;
    margin-top: 4px;
    color: ${theme.color.semantic.type.secondary};
  }

  .ant-form-item-explain-error {
    color: ${theme.color.semantic.feedback.type.error};
  }

  .ant-form-item-explain-warning {
    color: ${theme.color.semantic.feedback.type.warning};
  }

  .ant-form-item-additional {
    min-height: 0 !important;
  }

  .ant-form-item-margin-offset {
    display: none;
  }

  &.ant-form-vertical .ant-form-item-label {
    padding-bottom: 4px;
    line-height: 1;
  }
`;
export type FormItemProps = Omit<AntdFormItemProps, 'label'> & {
  label?: string;
  /** Overrides the label size that is otherwise derived from the form layout. */
  labelSize?: TypographySize;
  labelProps?: Pick<LabelProps, 'data-cy'>;
};

/** Styled Ant Design Form Component */
export function Form<T>(props: FormProps<T>): JSX.Element {
  const { layout = 'horizontal' } = props;

  return (
    <FormLayoutContext.Provider value={layout}>
      <StyledForm<FC<FormProps<T>>> {...props} labelWrap />
    </FormLayoutContext.Provider>
  );
}

/** Styled Ant Design Item Component */
export const Item: FC<FormItemProps> = ({ tooltip, label, labelSize, labelProps, ...restProps }) => {
  const layout = useContext(FormLayoutContext);
  const size = labelSize ?? DEFAULT_LABEL_SIZE_BY_LAYOUT[restProps.layout ?? layout ?? 'horizontal'];
  const type = DEFAULT_LABEL_TYPE_BY_LAYOUT[layout ?? 'horizontal'];

  return (
    <AntItem
      {...restProps}
      label={
        label && (
          <Typography.Label
            htmlFor={Array.isArray(restProps.name) ? restProps.name.join('_') : (restProps.name as string)}
            size={size}
            type={type}
            {...labelProps}
          >
            {label}
          </Typography.Label>
        )
      }
      tooltip={
        tooltip
          ? {
              title: <>{tooltip}</>,
              showArrow: false,
              icon: <Icon iconString="infoCircle" />,
              overlayStyle: { fontSize: theme.font.typography.metadata.fontSize, padding: '0.5rem' },
            }
          : undefined
      }
    />
  );
};

Form.Item = Item;
Form.useForm = useForm;
Form.useFormInstance = useFormInstance;
Form.useWatch = useWatch;
Form.ErrorList = ErrorList;
Form.List = List;
Form.Provider = Provider;
